<template>
	<div ref="toolbarRef" class="calendar-toolbar flex flex-wrap items-center justify-between gap-4">
		<div class="flex flex-wrap items-center gap-3">
			<n-button-group v-if="showNavigation">
				<JMButton type="secondary" :aria-label="$t(`common.misc.back`)" @click="emit('prev')">
					<template #icon>
						<Icon name="carbon:chevron-left" :size="18" />
					</template>
				</JMButton>
				<JMButton type="secondary" :label="$t(`common.misc.today`)" @click="emit('today')" />
				<JMButton type="secondary" :aria-label="$t(`common.misc.next`)" @click="emit('next')">
					<template #icon>
						<Icon name="carbon:chevron-right" :size="18" />
					</template>
				</JMButton>
			</n-button-group>

			<template v-if="isCompact">
				<n-popover
					trigger="hover"
					placement="bottom-start"
					:show-arrow="false"
					display-directive="show"
					class="p-3!"
				>
					<template #trigger>
						<n-badge :show="hasActiveFilters" dot type="success" :offset="[-3, 4]">
							<JMButton type="secondary" :aria-label="$t(`common.labels.filter`)">
								<template #icon>
									<Icon name="carbon:filter" :size="18" />
								</template>
							</JMButton>
						</n-badge>
					</template>
					<div class="flex w-70 flex-col gap-3">
						<OfficeSelect v-if="hasMultipleOffices" v-model="officeId" class="w-full" />
						<PatientSelect v-model="patientId" class="w-full" />
					</div>
				</n-popover>
			</template>
			<template v-else>
				<OfficeSelect v-if="hasMultipleOffices" v-model="officeId" class="w-full max-w-32 min-w-38" />
				<PatientSelect v-model="patientId" class="w-full max-w-32 min-w-38" />
			</template>
		</div>

		<div class="flex flex-wrap items-center gap-3">
			<div class="text-fs-lg leading-tight">
				<template v-if="period.mode === 'year'">
					<strong class="font-semibold">{{ period.year }}</strong>
				</template>
				<template v-else-if="period.mode === 'month'">
					<strong class="font-semibold capitalize">{{ period.month }}</strong>
					<span class="mx-2 font-normal">{{ period.year }}</span>
				</template>
				<template v-else>
					<strong class="font-semibold capitalize">{{ period.primary }}</strong>
					<span v-if="period.secondary" class="mx-2 font-normal">{{ period.secondary }}</span>
				</template>
			</div>

			<template v-if="showViewSwitcher">
				<n-select
					v-if="viewSwitcher === 'select'"
					:value="view"
					:options="viewOptions"
					:consistent-menu-width="false"
					class="w-32!"
					@update:value="emit('update:view', $event)"
				/>
				<n-radio-group v-else :value="view" size="medium" @update:value="emit('update:view', $event)">
					<n-radio-button v-for="option in viewOptions" :key="option.value" :value="option.value">
						{{ option.label }}
					</n-radio-button>
				</n-radio-group>
			</template>

			<AddAppointmentButton
				type="primary"
				size="medium"
				:label="$t(`appointments.common.create_embedded`)"
				:office-id
				icon-placement="right"
				:icon-only="isCompact"
				@saved="emit('saved')"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { I18nString } from "@/lang/config"
import { useElementSize } from "@vueuse/core"
import { NBadge, NButtonGroup, NPopover, NRadioButton, NRadioGroup, NSelect } from "naive-ui"
import { storeToRefs } from "pinia"
import { computed, toRefs, useTemplateRef } from "vue"
import { useI18n } from "vue-i18n"
import AddAppointmentButton from "@/components/calendar/AddAppointmentButton.vue"
import Icon from "@/components/common/Icon.vue"
import JMButton from "@/components/common/JMButton.vue"
import OfficeSelect from "@/components/offices/OfficeSelect.vue"
import PatientSelect from "@/components/patient/PatientSelect.vue"
import { useOfficesStore } from "@/stores/offices"

export type CalendarPeriodDisplay =
	| { mode: "year"; year: string }
	| { mode: "month"; month: string; year: string }
	| { mode: "day"; primary: string; secondary: string }
	| { mode: "range"; primary: string; secondary: string }

export type AvailableViews = "year" | "month" | "week" | "day" | "list"

export type CalendarViewType = "multiMonthYear" | "dayGridMonth" | "timeGridWeek" | "timeGridDay" | "listWeek"

export type CalendarViewSwitcher = "select" | "radio-group"

const props = withDefaults(
	defineProps<{
		view: CalendarViewType
		period: CalendarPeriodDisplay
		availableViews?: AvailableViews[]
		viewSwitcher?: CalendarViewSwitcher
		/** Mostra i pulsanti prev / oggi / next. */
		showNavigation?: boolean
		/** Mostra select o radio-group per cambiare vista. */
		showViewSwitcher?: boolean
		compact?: boolean
		autoCompact?: boolean
	}>(),
	{
		availableViews: () => ["month", "week", "day", "list"],
		viewSwitcher: "select",
		showNavigation: true,
		showViewSwitcher: true,
		compact: false,
		autoCompact: true
	}
)

const emit = defineEmits<{
	prev: []
	next: []
	today: []
	"update:view": [value: CalendarViewType]
	saved: []
}>()

const officeId = defineModel<number | null>("officeId", { default: null })
const patientId = defineModel<number | null>("patientId", { default: null })

const COMPACT_BREAKPOINT = 1088

const toolbarRef = useTemplateRef<HTMLElement>("toolbarRef")
const { width: toolbarWidth } = useElementSize(toolbarRef)

const { availableViews, autoCompact, compact } = toRefs(props)

const isCompact = computed(() => {
	if (autoCompact.value) {
		return toolbarWidth.value > 0 && toolbarWidth.value < COMPACT_BREAKPOINT
	}

	return compact.value
})

const { list: offices } = storeToRefs(useOfficesStore())

/** With a single office there is nothing to filter by, so the office select is hidden. */
const hasMultipleOffices = computed(() => offices.value.length > 1)

const hasActiveFilters = computed(() => officeId.value != null || patientId.value != null)

const { t } = useI18n()

const viewOptions = computed(() =>
	[
		{ value: "multiMonthYear" as const, label: t<I18nString>(`appointments.common.year`), type: "year" },
		{ value: "dayGridMonth" as const, label: t<I18nString>(`common.misc.month`), type: "month" },
		{ value: "timeGridWeek" as const, label: t<I18nString>(`common.misc.week`), type: "week" },
		{ value: "timeGridDay" as const, label: t<I18nString>(`common.misc.day`), type: "day" },
		{ value: "listWeek" as const, label: t<I18nString>(`appointments.common.list`), type: "list" }
	].filter(option => availableViews.value?.includes(option.type as AvailableViews))
)
</script>
